import * as React from 'react';
import { CalendarIcon } from 'lucide-react';

import { ItemTypeDisplay } from '@/components/ItemType/ItemTypeDisplay';
import { FieldSeparator } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';

interface SectionDividerProps {
  text: string;
}

export function SectionDivider({ text }: SectionDividerProps) {
  return <FieldSeparator className="tw:w-full">{text}</FieldSeparator>;
}

interface ItemTypeCellProps {
  name: string;
  imageUrl: string | null | undefined;
  hexCode: string | null | undefined;
  caption?: string;
}

export function ItemTypeCell({
  name,
  imageUrl,
  hexCode,
  caption,
}: ItemTypeCellProps) {
  return (
    <div className="tw:flex tw:items-center tw:gap-3">
      <ItemTypeDisplay
        alt={name}
        hexCode={hexCode}
        imageUrl={imageUrl}
        className="tw:size-10"
        showLargeImageOnHover
      />
      <div className="tw:flex tw:flex-col">
        <span className="tw:text-sm tw:font-medium">{name}</span>
        {caption && (
          <span className="tw:text-muted-foreground tw:text-xs">{caption}</span>
        )}
      </div>
    </div>
  );
}

interface FieldLabelProps {
  label: string;
  required?: boolean;
}

export function FieldLabel({ label, required }: FieldLabelProps) {
  return (
    <p className="tw:text-foreground tw:text-sm tw:font-medium">
      {label}
      {required && <span className="tw:text-destructive tw:ml-0.5">*</span>}
    </p>
  );
}

interface TextFieldProps {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  hidden?: boolean;
  error?: string;
  className?: string;
  fieldKey?: string;
}

export function TextField({
  label,
  value,
  onChange,
  required,
  placeholder,
  disabled,
  hidden,
  error,
  className,
  fieldKey,
}: TextFieldProps) {
  if (hidden) return null;

  return (
    <div
      className={cn('tw:flex tw:flex-col tw:gap-1.5', className)}
      data-field={fieldKey}
    >
      <FieldLabel label={label} required={required} />
      <Input
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={!onChange}
        aria-invalid={!!error}
      />
      {error && <p className="tw:text-destructive tw:text-xs">{error}</p>}
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  options: Array<{ label: string; value: string }>;
  disabled?: boolean;
  className?: string;
}

export function SelectField({
  label,
  value,
  onValueChange,
  placeholder,
  options,
  disabled,
  className,
}: SelectFieldProps) {
  return (
    <div className={cn('tw:flex tw:flex-col tw:gap-1.5', className)}>
      <FieldLabel label={label} />
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger className="tw:w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent align="start">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface DateFieldProps {
  label: string;
  value: string;
}

export function DateField({ label, value }: DateFieldProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <FieldLabel label={label} />
      <div className="tw:relative">
        <Input value={value} readOnly className="tw:pr-10" />
        <CalendarIcon className="tw:text-muted-foreground tw:pointer-events-none tw:absolute tw:right-3 tw:top-1/2 tw:size-4 tw:-translate-y-1/2" />
      </div>
    </div>
  );
}

interface CommentFieldProps {
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  error?: string;
  fieldKey?: string;
}

export function CommentField({
  value,
  onChange,
  disabled,
  placeholder,
  error,
  fieldKey,
}: CommentFieldProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5" data-field={fieldKey}>
      <Textarea
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        readOnly={!onChange}
        disabled={disabled}
        aria-invalid={!!error}
        className="tw:min-h-16 tw:resize-none"
        placeholder={placeholder ?? i18n.t('Order:commentPlaceholder')}
      />
      {error && <p className="tw:text-destructive tw:text-xs">{error}</p>}
    </div>
  );
}
